import React, { useContext, useState, Children } from 'react';

import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import RemoveIcon from '@mui/icons-material/Remove';
import LoadingButton from '@mui/lab/LoadingButton';
import { randomId } from '@mui/x-data-grid-generator';

import TextFieldCustom from 'components/object/formElement/TextFieldCustom';
import SelectAutoCustom from 'components/object/formElement/SelectAutoCustom';
import DesktopDatePickerCustom from 'components/object/formElement/DesktopDatePickerCustom';
import DigiformaStaffingBadge from 'components/object/DigiformaStaffingBadge';

import { ContextBlock } from 'context/ContextBlock';
import { ContextTrainingSearch } from 'context/ContextTrainingSearch';

const TrainingSessionBlock = () => {

  //context
  const {
    TRAINING_THEMATIC_LIST_URL
  }  = useContext(ContextBlock);

  const {
    getTrainingThematicById,
    checkTrainingSessionsDateInError,
    isNDaysInvalid, checkTrainingSessionsDaysInError,
    trainingSessions, setTrainingSessions,
    removeTrainingSessions,
    saveTrainingSessions, validTrainingSessions,
    saveTrainingSearch
  } = useContext(ContextTrainingSearch);

  const [loading, setLoading] = useState(false);

  // Rows are never re-sorted while editing (they would jump around under the user's cursor):
  // sorting only happens on page load and after saving (see ContextTrainingSearch).
  const updateTrainingSearch = (subsessionId, data) => {
    setTrainingSessions(trainingSessions.map((item) => {
      if (item.subsessionId == subsessionId) {
        return { ...item, ...data };
      }
      return item;
    }));
  }

  const updateTrainingSearchById = (id, data) => {
    setTrainingSessions(trainingSessions.map((item) => {
      if (item.id == id) {
        return data;
      }
      return item;
    }));
  }

  const setDateSessionStartHandler = (date, subsession) => {
    updateTrainingSearchById(
      subsession.id,
      { ...subsession, startDate: date }
    );
  }

  const setDateSessionEndHandler = (date, subsession) => {
    updateTrainingSearchById(
      subsession.id,
      { ...subsession, endDate: date }
    );
  }

  const setNDaysHandler = (event, subsession) => {
    updateTrainingSearch(
      subsession.subsessionId,
      { nDays: event.target.value != '' ? parseFloat(event.target.value) : '' }
    );
  }

  const setTrainingThematicHandler = (trainingThematicId, alias, label, item, subsession) => {
    if (trainingThematicId !== null) {
      const trainingThematic = getTrainingThematicById(trainingThematicId);
      updateTrainingSearch(
        subsession.subsessionId,
        { trainingThematicId: trainingThematic.id, nDays: trainingThematic.durationInDays, tags: trainingThematic.tags }
      );
    }
  }

  function createTrainingThematicHandler(data) {
    return { id: data.id, title: data.name, alias: data.name, label: data.name, labelValue: data.name };
  }

  const saveSessionsAndSearch = async () => {
    const dateInError = checkTrainingSessionsDateInError();
    if (dateInError) return;
    const daysInError = checkTrainingSessionsDaysInError();
    if (daysInError) return;

    setLoading(true);
    await saveTrainingSessions();
    await saveTrainingSearch();
    setLoading(false);
  }

  const handleValidTrainingSessions = async () => {
    setLoading(true);
    await validTrainingSessions();
    setLoading(false);
  }

  const handleRemoveSession = async (subsession) => {
    if(!isNaN(subsession.id)){
      //If the subsession id is a number, the session has already been saved to DB => it has to be deleted
      const response = await removeTrainingSessions(subsession.id)
      if(response?.success == true){
        const filtered = trainingSessions.filter((session) => session.id !== subsession.id);
        setTrainingSessions(filtered);
      }
    }else{
    const filtered = trainingSessions.filter((session) => session.id !== subsession.id);
    setTrainingSessions(filtered);
    }
  }

  const handleAddSession = (subsession) => {
    const newSession = {
      trainingThematicId: subsession.trainingThematicId,
      subsessionId: subsession.subsessionId,
      nDays: subsession.nDays,
      tags: subsession.tags,
      name: subsession.name,
      digiformaStaffing: subsession.digiformaStaffing,
      id: randomId(),
      //new date
      startDate: null,
      endDate: null
    };

    // Insert right after the module's last row: rows of a module must stay grouped, the first one
    // of each group holding the module inputs (thematic, days, add button)
    let lastModuleRowIndex = -1;
    trainingSessions.forEach((item, index) => {
      if (item.subsessionId == subsession.subsessionId) lastModuleRowIndex = index;
    });
    const rows = [...trainingSessions];
    rows.splice(lastModuleRowIndex + 1, 0, newSession);
    setTrainingSessions(rows);
  }


  const firstRow = [];
  return (
    <div className="trainingSessions-block">
      <div className="details-block">
        <div className="table">
          <div className="table-row th">
            <div className="table-cell add">&nbsp;</div>
            <div className="table-cell date">Du</div>
            <div className="table-cell date">Au</div>
            <div className="table-cell jour">Jours</div>
            <div className="table-cell them">Thématique</div>
            <div className="table-cell comp">Compétence</div>
            <div className="table-cell df">&nbsp;</div>
          </div>

          {trainingSessions.length > 0 ? Children.toArray(trainingSessions.map((row, index) => {
            let isFirstRow = false;
            if (!firstRow.includes(row.subsessionId)) {
              isFirstRow = true;
              firstRow.push(row.subsessionId);
            }
            const dateInError = row.startDate && row.endDate && row.endDate.isBefore(row.startDate, 'day');
            const daysInError = isNDaysInvalid(row.nDays);

            return (
            <div className="table-row">
              <div className="table-cell add">
                <Tooltip title={isFirstRow ? "Ajouter une date" : "Supprimer cette date" }>
                  <IconButton 
                    color="primary" 
                    size="small" 
                    onClick={() => isFirstRow ? handleAddSession(row) : handleRemoveSession(row) }
                  >
                    {isFirstRow ? <AddIcon /> : <RemoveIcon/> }
                  </IconButton>
                </Tooltip>
              </div>
              <div className="table-cell date">
                <DesktopDatePickerCustom
                  label=""
                  value={row.startDate}
                  onChange={setDateSessionStartHandler}
                  changeParams={row}
                  error={dateInError}
                />
              </div>
              <div className="table-cell date">
                <DesktopDatePickerCustom
                  label=""
                  value={row.endDate}
                  onChange={setDateSessionEndHandler}
                  changeParams={row}
                  error={dateInError}
                />
              </div>
              <div className="table-cell jour">
                {isFirstRow && 
                <TextFieldCustom
                  label=""
                  fullWidth
                  type="decimal"
                  id="input-days"
                  variant="standard"
                  value={row.nDays}
                  onChange={setNDaysHandler}
                  changeParams={row}
                  className="input"
                  error={daysInError}
                  slotProps={{
                    input: {
                      disableUnderline: true
                    }
                  }}
                />
                }
              </div>
              <div className="table-cell them">
                {isFirstRow && 
                <SelectAutoCustom
                  title=""
                  api={TRAINING_THEMATIC_LIST_URL}
                  apiParam=""
                  apiParamValue=""
                  defaultAlias="XXX"
                  dataValue={row.trainingThematicId ? getTrainingThematicById(row.trainingThematicId)?.name ?? row.name : row.name}
                  setDataValue={setTrainingThematicHandler}
                  creator={createTrainingThematicHandler}
                  changeParams={row}
                  error={!(row.trainingThematicId !== null)}
                  disableClearable={true}
                />
                }
              </div>
              <div className="table-cell comp">{isFirstRow && row.tags?.map(t => t.name).join(', ')}</div>
              <div className="table-cell df">
                {isFirstRow &&
                <DigiformaStaffingBadge modules={[{ name: row.name, digiformaStaffing: row.digiformaStaffing }]} />
                }
              </div>
            </div>
            );
          })) : 
            <div className="table-cell no-data">Pas de données</div>
          }
        </div>

        <div className="block-button">
          <Stack direction="row">
            <div>
              <Stack spacing={2} direction="row">
                <LoadingButton variant="contained" color="primary" loading={loading} disabled={loading} onClick={saveSessionsAndSearch}>Enregistrer</LoadingButton>
                <LoadingButton variant="contained" color="success" loading={loading} disabled={loading} onClick={handleValidTrainingSessions}>Rechercher</LoadingButton>
              </Stack>
            </div>
          </Stack>
        </div>
      </div>
    </div>
  );
}

export default TrainingSessionBlock;